import { DeleteOutlined, GlobalOutlined } from '@ant-design/icons/lib/icons';
import { Button, Form, Input, Popconfirm, Select, Tooltip } from 'antd';
import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { every, isNumber } from 'underscore';
import LoadingContext from '../../../../context/loadingContext/LoadingContext';
import { usePointStore } from '../../../../store/pointStore';
import { IUTMItem, MarkerItem } from '../../../../types/home';
import coordinateFn from '../../../../utils/coordinate';
import { withLoading } from '../../../common/Loading/withLoading';
import styles from '../Style/CoordinatesInfo.module.scss';

const { Option } = Select;

export function CoordinatesInfo(props: {
  id: string;
  index: number;
  name: string;
  x: number | null;
  y: number | null;
  changeCoords: (coords: number[]) => any;
  addCoords: (coords: number[]) => void;
  disableCoordinateChange?: boolean | undefined;
  resetCoordsKey: string;
  setUTMItem: (item: IUTMItem) => void;
  UTMItem: IUTMItem;
  showUTM: boolean;
  setShowUTM: (item: boolean) => void;
  onPointNameChange: (event: ChangeEvent<HTMLInputElement>, id: string) => void;
  onPointsDelete: (id: string) => void;
  markerPoint?: MarkerItem;
  onMark?: (bool: boolean) => void;
}) {
  const [form] = Form.useForm();
  const {
    id,
    index,
    name,
    x,
    y,
    changeCoords,
    addCoords,
    disableCoordinateChange,
    resetCoordsKey,
    UTMItem = {
      east: '0',
      north: '0',
      zone: '5N',
    },
    setUTMItem,
    showUTM = false,
    setShowUTM,
    onPointNameChange,
    onPointsDelete,
    markerPoint,
    onMark,
  } = props;
  const loadingContext = useContext(LoadingContext);
  const setAddPointName = usePointStore((state) => state.setAddPointName);
  const [xCoord, setXCoord] = useState<number | string | null>(props.x);
  const [yCoord, setYCoord] = useState<number | string | null>(props.y);
  const [submitEnabled, setSubmitEnabled] = useState(false);
  const [coordsTooltip, setCoordsTooltip] = useState('');

  useEffect(() => {
    form.setFieldsValue({
      name: markerPoint ? markerPoint.label : 'P' + id,
    });
  }, [markerPoint, id]);

  useEffect(() => {
    setXCoord(x);
    setYCoord(y);
    form.setFieldsValue({
      longitude: x,
      latitude: y,
    });
  }, [x, y]);

  useEffect(() => {
    // The user canceled to change coordinates
    if (name === resetCoordsKey) {
      setXCoord(x);
      setYCoord(y);
      form.setFieldsValue({
        longitude: x,
        latitude: y,
      });
    }
  }, [name, resetCoordsKey, x, y]);

  useEffect(() => {
    let enableSubmit =
      (!disableCoordinateChange &&
        xCoord !== null &&
        yCoord !== null &&
        x !== xCoord) ||
      y !== yCoord;
    if (enableSubmit) {
      const numericValue = Number(xCoord);
      enableSubmit = !isNaN(numericValue);
      if (enableSubmit) {
        enableSubmit = numericValue >= -180 && numericValue <= 180; //showUTM ? numericValue >= 160000 && numericValue <= 834000 : numericValue >= -180 && numericValue <= 180;
      }
    }
    if (enableSubmit) {
      const numericValue = Number(yCoord);
      enableSubmit = !isNaN(numericValue);
      if (enableSubmit) {
        enableSubmit = numericValue >= -180 && numericValue <= 180; //showUTM ? numericValue >= 0 && numericValue <= 10000000 : numericValue >= -90 && numericValue <= 90;
      }
    }
    setSubmitEnabled(enableSubmit);

    setCoordsTooltip(
      disableCoordinateChange
        ? 'Remove dataset(s) from cart to change coordinates'
        : ''
    );
  }, [xCoord, yCoord, disableCoordinateChange, x, y]);

  // Only allow alphanumeric characters that work in filenames
  const validateInput = (event: ChangeEvent<HTMLInputElement>) => {
    const label = event.target.value;
    event.stopPropagation();
    const specialCharRegex = new RegExp(/[^a-zA-Z0-9.,_ ()-]/g);
    if (specialCharRegex.test(label)) {
      return false;
    }
    return true;
  };

  const getUTM = useCallback(() => {
    if (xCoord !== null && yCoord !== null) {
      const result: any = coordinateFn.convertLatLonToUTM(
        String(xCoord),
        String(yCoord)
      );
      setUTMItem({
        east: result.eastingValue,
        north: result.northingValue,
        zone: result.SelectedZone,
      });
      form.setFieldsValue({
        easting: result.eastingValue,
        northing: result.northingValue,
        utmzone: result.SelectedZone,
      });
    }
  }, [xCoord, yCoord]);

  useEffect(() => {
    getUTM();
  }, [getUTM]);

  function onUTMChange(coords: string[], zone: string) {
    if (!every(coords, (c) => isNumber(+c) && !isNaN(+c))) {
      return;
    }

    setUTMItem({
      east: coords[0],
      north: coords[1],
      zone: zone,
    });
    const result = coordinateFn.convertUTMToLatLng(
      String(coords[0]),
      String(coords[1]),
      zone
    );

    if (!result) {
      return;
    }
    setXCoord(result[0]);
    setYCoord(result[1]);
    form.setFieldsValue({
      longitude: result[0],
      latitude: result[1],
    });
  }

  // do convert coordinates
  function toggleCoordinateDisplay() {
    setShowUTM(!showUTM);
  }

  const submitCoordinates = useCallback(() => {
    if (xCoord !== null && yCoord !== null) {
      if (x !== null && y !== null) {
        changeCoords([Number(xCoord), Number(yCoord)]);
      } else {
        addCoords([Number(xCoord), Number(yCoord)]);
      }
    }
  }, [xCoord, yCoord, changeCoords, addCoords, x, y]);

  const onXCoordChange = useCallback((value: string) => {
    setXCoord(value);
  }, []);

  const onYCoordChange = useCallback((value: string) => {
    setYCoord(value);
  }, []);

  const checkLongitude = (_rule: any, value: string) => {
    const message = 'Allowed: [-180:180]';
    if (!value) {
      setSubmitEnabled(false);
      return Promise.reject(message);
    }
    const numericValue = Number(value);
    if (isNaN(numericValue) || numericValue < -180 || numericValue > 180) {
      return Promise.reject(message);
    }
    return Promise.resolve();
  };

  const checkLatitude = (_rule: any, value: string) => {
    const message = 'Allowed: [-90:90]';
    if (!value) {
      setSubmitEnabled(false);
      return Promise.reject(message);
    }
    const numericValue = Number(value);
    if (isNaN(numericValue) || numericValue < -90 || numericValue > 90) {
      return Promise.reject(message);
    }
    return Promise.resolve();
  };

  const checkEasting = (_rule: any, value: string) => {
    const message = '[160000:834000]';
    if (!value) {
      setSubmitEnabled(false);
      return Promise.reject(message);
    }
    const numericValue = Number(value);
    if (isNaN(numericValue) || numericValue < 160000 || numericValue > 834000) {
      return Promise.reject(message);
    }
    return Promise.resolve();
  };

  const checkNorthing = (_rule: any, value: string) => {
    const message = '[0:10000000]';
    if (!value) {
      setSubmitEnabled(false);
      return Promise.reject(message);
    }
    const numericValue = Number(value);
    if (isNaN(numericValue) || numericValue < 0 || numericValue > 10000000) {
      return Promise.reject(message);
    }
    return Promise.resolve();
  };

  const handelPointNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (validateInput(e)) {
      if (markerPoint) {
        onPointNameChange(e, markerPoint.id);
      } else {
        setAddPointName(e.target.value);
      }
    }
  };

  return (
    <Form form={form}>
      <div className={styles.container}>
        <Form.Item
          labelCol={{ span: 24 }}
          wrapperCol={{ span: 24 }}
          name='name'
          label='Name'
        >
          <Input
            id={`PointPanel-Name_Input_${index}`}
            data-testid='PointPanel-Name_Input'
            size='small'
            maxLength={25}
            disabled={disableCoordinateChange}
            onChange={handelPointNameChange}
          />
        </Form.Item>
        {!showUTM ? (
          <>
            <Tooltip title={coordsTooltip}>
              <Form.Item
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                name='longitude'
                label='Longitude [°E]'
                rules={[{ validator: checkLongitude }]}
              >
                <Input
                  id={`PointPanel-Longitude_Input_${index}`}
                  data-testid='PointPanel-Longitude_Input'
                  bordered={true}
                  placeholder=''
                  size='small'
                  key='Longitude'
                  value={xCoord !== null ? xCoord : undefined}
                  disabled={disableCoordinateChange}
                  onChange={(e) => onXCoordChange(e.target.value)}
                />
              </Form.Item>
            </Tooltip>
            <Tooltip title={coordsTooltip}>
              <Form.Item
                labelCol={{ span: 24 }}
                wrapperCol={{ span: 24 }}
                name='latitude'
                label='Latitude [°N]'
                rules={[{ validator: checkLatitude }]}
              >
                <Input
                  id={`PointPanel-Latitude_Input_${index}`}
                  data-testid='PointPanel-Latitude_Input'
                  bordered={true}
                  size='small'
                  placeholder=''
                  key='Latitude'
                  value={yCoord !== null ? yCoord : undefined}
                  disabled={disableCoordinateChange}
                  onChange={(e) => onYCoordChange(e.target.value)}
                />
              </Form.Item>
            </Tooltip>
          </>
        ) : (
          <>
            <Form.Item
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              name='easting'
              label='Easting [m]'
              rules={[{ validator: checkEasting }]}
            >
              <Input
                id={`PointPanel-Easting_Input_${index}`}
                data-testid='PointPanel-Easting_Input'
                size='small'
                bordered={true}
                placeholder=''
                key='Easting'
                value={UTMItem.east}
                disabled={disableCoordinateChange}
                onChange={(e) =>
                  onUTMChange([e.target.value, UTMItem.north], UTMItem.zone)
                }
              />
            </Form.Item>
            <Form.Item
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              name='northing'
              label='Northing [m]'
              rules={[{ validator: checkNorthing }]}
            >
              <Input
                id={`PointPanel-Northing_Input_${index}`}
                size='small'
                bordered={true}
                key='Northing'
                placeholder=''
                value={UTMItem.north}
                disabled={disableCoordinateChange}
                onChange={(e) =>
                  onUTMChange([UTMItem.east, e.target.value], UTMItem.zone)
                }
              />
            </Form.Item>
            <Form.Item
              labelCol={{ span: 24 }}
              wrapperCol={{ span: 24 }}
              name='utmzone'
              label='UTM Zone'
            >
              <Select
                id={`PointPanel-UTM_Input_${index}`}
                data-testid='PointPanel-UTM_Input'
                size='small'
                className={styles.utmSelect}
                disabled={
                  !UTMItem.east || !UTMItem.north || disableCoordinateChange
                }
                bordered={false}
                key='Zone'
                value={UTMItem.zone}
                onChange={(value) =>
                  onUTMChange([UTMItem.east, UTMItem.north], value)
                }
              >
                {coordinateFn.UTMZoneArray.map((zone) => {
                  return (
                    <Option key={zone} value={zone}>
                      {zone}
                    </Option>
                  );
                })}
              </Select>
            </Form.Item>
          </>
        )}
        <Button
          id={`PointPanel-Submit_Button_${index}`}
          data-testid='PointPanel-Submit_Button'
          type='primary'
          className={styles.SubmitPointButton}
          onClick={submitCoordinates}
          disabled={!submitEnabled}
          size='small'
        >
          Submit
        </Button>
        <div className={styles.buttonContainer}>
          <GlobalOutlined
            id={`PointPanel - UTM_Button_${index}`}
            className={styles.transformBtn}
            onClick={toggleCoordinateDisplay}
          />
          {markerPoint && (
            <Popconfirm
              placement='left'
              title={'Are you sure to delete this Point?'}
              onConfirm={() =>
                withLoading(
                  () => onPointsDelete(markerPoint.id),
                  loadingContext,
                  {
                    source: 'onPointsDelete',
                  }
                )
              }
              okText='Yes'
              cancelText='No'
            >
              <DeleteOutlined
                id={`PointPanel-Delete_Point_${index}`}
                className={styles.transformBtn}
              />
            </Popconfirm>
          )}
          {!markerPoint && (
            <DeleteOutlined
              id={`PointPanel-Delete_Point_${index}`}
              onClick={() =>
                withLoading(() => onMark?.(false), loadingContext, {
                  source: 'onMarkDelete',
                })
              }
              className={styles.transformBtn}
            />
          )}
        </div>
      </div>
    </Form>
  );
}
